import React, { useState, useEffect } from 'react';
import { X, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { Client } from '../../types/client';
import { User } from '../../types/api';
import { clientService } from '../../services';
import { apiService } from '../../services/api';
import { EmployeePercentageSelector, EmployeeAssignment, UserOption } from '../shared/EmployeePercentageSelector';

interface ClientBulkAssignModalProps {
  client: Client;
  onClose: () => void;
  onSuccess: () => void;
}

export const ClientBulkAssignModal: React.FC<ClientBulkAssignModalProps> = ({
  client,
  onClose,
  onSuccess
}) => {
  const [employees, setEmployees] = useState<EmployeeAssignment[]>([]);
  const [applyTo, setApplyTo] = useState<'active_accounts_only' | 'all_accounts'>('active_accounts_only');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);

  useEffect(() => {
    loadAvailableUsers();
  }, []);

  const loadAvailableUsers = async () => {
    setLoading(true);
    setUsersError(null);

    try {
      const response = await apiService.getEmployees({
        per_page: 1000,
        is_active: true
      });

      const users: UserOption[] = response.data.map((user: User) => ({
        id: user.id,
        name: user.name,
        email: user.email
      }));

      setAvailableUsers(users);
    } catch (err: any) {
      setUsersError(err.response?.data?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (employees.length === 0) {
      setError('Please add at least one employee');
      return;
    }

    const hasInvalidPercentage = employees.some(emp => Number(emp.commission_percentage || 0) <= 0);
    if (hasInvalidPercentage) {
      setError('Each employee must have a percentage greater than 0%.');
      return;
    }

    setSubmitting(true);

    try {
      await clientService.bulkAssignEmployees(
        client.id,
        employees,
        applyTo,
        overwriteExisting
      );

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign employees');
    } finally {
      setSubmitting(false);
    }
  };

  const allPercentagesValid = employees.every(emp => Number(emp.commission_percentage || 0) > 0);
  const accountCount = applyTo === 'all_accounts' ? (client.accounts_count || 0) : (client.active_accounts_count || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Bulk Assign Employees</h3>
            <p className="text-sm text-gray-600 mt-1">{client.client_name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={submitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Info Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Users className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">
                  Assign employees to all {client.client_name} accounts
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  This will assign the selected employees to multiple accounts at once
                </p>
              </div>
            </div>
          </div>

          {/* Apply To Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Apply To:
            </label>
            <div className="space-y-2">
              <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="applyTo"
                  value="active_accounts_only"
                  checked={applyTo === 'active_accounts_only'}
                  onChange={(e) => setApplyTo(e.target.value as 'active_accounts_only')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Active accounts only ({client.active_accounts_count || 0}) <span className="text-green-600">RECOMMENDED</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Only assign to currently active accounts
                  </div>
                </div>
              </label>

              <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="applyTo"
                  value="all_accounts"
                  checked={applyTo === 'all_accounts'}
                  onChange={(e) => setApplyTo(e.target.value as 'all_accounts')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    All accounts ({client.accounts_count || 0})
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Assign to all accounts including inactive ones
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Employee Selector */}
          <EmployeePercentageSelector
            employees={employees}
            onChange={setEmployees}
            availableUsers={availableUsers}
            loading={loading}
            error={usersError}
          />

          {/* Overwrite Option */}
          <div className="space-y-3">
            <label className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={overwriteExisting}
                onChange={(e) => setOverwriteExisting(e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">
                  Overwrite existing assignments
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  If unchecked, will skip employees that are already assigned to accounts
                </div>
              </div>
            </label>
          </div>

          {/* Warning */}
          {accountCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-900">
                    This will affect {accountCount} account{accountCount !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    {employees.length} employee{employees.length !== 1 ? 's' : ''} will be assigned to each account
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !allPercentagesValid || employees.length === 0 || loading}
            className="btn btn-primary flex items-center space-x-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Assigning...</span>
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                <span>Assign to {accountCount} Account{accountCount !== 1 ? 's' : ''}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
